import {Arc, Selection} from 'd3';
import * as d3 from 'd3';
import _ from 'underscore';

import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {Sequence} from 'components/Sequence';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import {RingView} from 'molbio/maps/circular/RingView';
import * as mapsutil from 'molbio/maps/MapsUtil';
import {RegionSelectable} from 'molbio/maps/selectables/Selectables';
import {NEAR_BLACK} from 'ui-library/Colors';

export type SegmentRingSelection<TSegmentsRingData> = Selection<
  SVGGElement,
  TSegmentsRingData,
  SVGGElement,
  unknown
>;

export interface SegmentsRingData extends RegionSelectable<Sequence> {
  color(): string;
  hover(): boolean;
  uid(): string;
}

export default abstract class SegmentsRingView<
  TLayout extends RingLayout,
  TSegmentsRingData extends SegmentsRingData,
> extends RingView<TLayout> {
  renderedData!: null | Array<TSegmentsRingData>;
  recomputeLayers!: boolean;
  rings?: number | null;
  abstract singularClassName: string;

  data(): Array<TSegmentsRingData> {
    return [];
  }

  initialize(options: RingComponentOptions<TLayout>): void {
    super.initialize(options);
    this.renderedData = null;
    this.recomputeLayers = true;
  }

  contextMenuData(): Array<string> {
    return [];
  }

  contextMenuOptions(d: unknown): MenuChoicesOptions {
    return {};
  }

  arc(d: TSegmentsRingData): Arc<unknown, unknown> {
    return d3.arc();
  }

  fill(d: TSegmentsRingData): string {
    return d.color();
  }

  opacity(d: TSegmentsRingData): number {
    if (d.hover() || d.selected()) {
      return 1.0;
    } else {
      return 0.7;
    }
  }

  fontColor(d: TSegmentsRingData): string {
    return NEAR_BLACK;
  }

  fontWeight(d: TSegmentsRingData): number {
    if (d.hover() || d.selected()) {
      return 700;
    } else {
      return 400;
    }
  }

  stroke(d: TSegmentsRingData): string {
    return d3.rgb(d.color()).darker().formatHex();
  }

  strokeWidth(d: TSegmentsRingData): number {
    if (d.hover()) {
      return 1.5;
    } else {
      return 1.000001;
    }
  } // see https://code.google.com/p/chromium/issues/detail?id=339297

  handleHover(model: TSegmentsRingData, value: boolean): void {
    const id = model.uid();
    const root = d3.select(this.el).selectAll('.individual-ring').filter(`[id='${id}']`);
    root
      .select('.ring-shape')
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .attr('stroke-width', this.strokeWidth)
      .transition()
      .duration(25)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .attr('fill-opacity', this.opacity)
      .attr('fill', (d: TSegmentsRingData) => this.fill(d));
  }

  registerPieceHandlers(selector: Selection<SVGGElement, TSegmentsRingData, SVGGElement, unknown>): void {
    let sequence = this.model.get('sequence');
    const bindStopPropagation = (f: (e: MouseEvent, d: TSegmentsRingData) => void, ref: this) =>
      function (e: MouseEvent, d: TSegmentsRingData) {
        if (f != null) {
          e.stopPropagation();
          return f.call(ref, e, d);
        }
      };

    selector
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .on('mousedown.segmentring', bindStopPropagation(this.pieceClickHandler, this))
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .on('dblclick.segmentring', bindStopPropagation(this.pieceDblClickHandler, this))
      .on('mouseover.segmentring', (e, d) => d.set({hover: true}))
      .on('mouseout.segmentring', (e, d) => d.set({hover: false}))
      .on('contextmenu.segmentring', (e, d) => {
        if (this.contextMenuData != null) {
          sequence = this.model.get('sequence');
          if (selectioncontextmenu.open(e, sequence, this.contextMenuData(), this.contextMenuOptions(d))) {
            if (this.shouldCaptureLeftClick()) {
              e.stopPropagation();
              this.pieceClickHandler(e, d);
            }
          }
        }
      });
  }

  handleDraw(selector: Selection<SVGGElement, TSegmentsRingData, SVGGElement, unknown>): void {
    selector
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .attr('stroke-width', this.strokeWidth)
      .attr('stroke', (d) => this.stroke(d))
      .attr('fill', (d) => this.fill(d))
      .attr(
        'd',
        ((ref) =>
          function (d: TSegmentsRingData) {
            const layer = d.get('annularLayer');

            const start = d.start();
            const end = d.end();

            const angles = ref.getAngles(start, end);
            const arc = ref.arc(d);
            const radius = ref.layerBounds(layer);
            if (arc != null) {
              arc
                .innerRadius(radius.innerRadius)
                .outerRadius(radius.outerRadius)
                .startAngle(angles.startAngle)
                .endAngle(angles.endAngle);
              return arc(d);
            } else {
              return '';
            }
          })(this)
      )
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .attr('fill-opacity', this.opacity);
  }

  computeMinRings(): number {
    // This should be an overestimate, to ensure things don't display weirdly when we get to this min size.
    if (!this.layout.get('visible')) {
      return 0;
    }

    const data = this.renderedData ?? this.data();
    const maxLayer = mapsutil.getMax(data);
    // TODO: figure out what triggers this now.
    // FIXME:
    // When del is called on the server, primers, annotations, and seq length all change.
    // One of the listeners for primers triggers layerChange which triggers a redraw of the plasmid,
    //   despite the fact that annotation wrappers haven't yet updated.
    // Rendering should happen after the model is CORRECT... either by a defer/debounce or triggered
    // by an event after the model is correct.

    if (Number.isNaN(maxLayer)) {
      return 0;
    } else {
      return maxLayer + 1;
    }
  }

  setConstraints(outerRadius: number): void {
    this.update();
  }

  baseLine(layer: number): number {
    return this.radius(layer);
  }

  updateLayers(data: Array<TSegmentsRingData>): void {
    if (data.length === 0) {
      this.storeBounds(0);
      return;
    }

    const layerData = mapsutil.getLayers(data, {sequence: this.model.get('sequence')});
    // eslint-disable-next-line no-restricted-syntax
    _.each(layerData.layers, (obj) => obj.model.set('annularLayer', obj.layer));

    this.storeBounds(layerData.max + 1);
  }

  // Default
  storeBounds(rings?: null | number): void {
    if (rings == null) {
      ({rings} = this);
    }
    this.rings = rings;
    const outerRadius = this.getInnerRadius() + this.layout.get('ringHeight') * this.rings!;
    this.layout.set('outerRadius', outerRadius);
    this.updateRadius();
  }

  zeroBounds(): void {
    this.layout.set('outerRadius', this.layout.get('innerRadius'));
    this.updateRadius();
  }

  stopListening(): void {
    super.stopListening();
    d3.select(this.el).selectAll('.ring-shape').on('.segmentring', null);
  }

  enterRing(enter: SegmentRingSelection<TSegmentsRingData>): SegmentRingSelection<TSegmentsRingData> {
    enter
      .append('svg:path')
      .classed('ring-shape', true)
      .classed(this.singularClassName, true)
      .attr('shape-rendering', 'geometricPrecision');
    this.registerPieceHandlers(enter);
    return enter;
  }

  eachRing(root: SegmentRingSelection<TSegmentsRingData>): void {
    root.attr('id', (d) => d.uid());
    root.each(
      ((ref: this) =>
        function (d: TSegmentsRingData) {
          // @ts-expect-error
          ref.handleDraw(d3.select(this).select('.ring-shape'), d);
        })(this)
    );
  }

  invalidateData(): void {
    this.renderedData = null;
    this.recomputeLayers = true;
  }

  invalidateLayers(): void {
    this.recomputeLayers = true;
  }

  updateData(): void {
    this.renderedData = this.data();
  }

  hideHandler(): void {}

  redraw(): void {
    // Refresh data, and recompute layers.
    if (this.layout.get('visible')) {
      this.$el.show();
    } else {
      this.$el.hide();
      this.hideHandler();
      this.zeroBounds();
      return;
    }
    if (this.renderedData == null) {
      this.updateData();
    }

    if (this.recomputeLayers) {
      this.updateLayers(this.renderedData!);
    } else {
      // Even if we don't need to recompute layers, we still need to update the bounds.
      // Passing null for total rings will use the last value of total rings.
      this.storeBounds(null);
    }

    let root = d3
      .select(this.el)
      .selectAll('.individual-ring')
      .data(this.renderedData!) as SegmentRingSelection<TSegmentsRingData>;

    // Exit on old rings
    const exit = root.exit();
    exit.on('.segmentring', null).remove();

    // Enter on new rings
    const enter = root
      .enter()
      .append('svg:g')
      .classed('individual-ring', true) as SegmentRingSelection<TSegmentsRingData>;

    root = root.merge(enter);
    root = root.merge(this.enterRing(enter));

    // Rerender current rings.
    this.eachRing(root);
  }
}
